import type { FeatureMiddleware } from '@cast/api-client';
import {
  demoWoopLimitRangesList,
  demoWoopResourceQuotasList,
  demoWoopScalingPoliciesList,
  demoWoopWorkloadDetails,
  demoWoopWorkloadFilters,
  demoWoopWorkloadsList,
  demoWoopWorkloadSpec,
  demoWoopWorkloadsSummary,
  demoWoopWorkloadsSummaryMetrics,
} from '@cast/fixtures';
import {
  OptimizationWorkloadDetailsResponse,
  WorkloadOptimizationAgentStatus,
  WorkloadOptimizationEvent,
  WorkloadSpecResponse,
} from '@cast/types';
import { DEMO_CLUSTER_ID } from '@cast/utils';

import {
  getIsClusterDemoMode,
  getIsOpDemoMode,
} from '@cast/infrastructure-demo';

import {
  rejectDemoClusterRequest,
  rejectWithErrorMessage,
  handleOpDemoInterceptor,
  isSimulatedDemoClusterRequest,
  opDemoLoader,
  getTourStepIdFromUrl,
} from '../utils';

const isWoopDemoRequest = (params: unknown) =>
  getIsClusterDemoMode() || isSimulatedDemoClusterRequest(params);

/** OP tour states that ship workloadOptimization.getWorkloads fixtures. */
const OP_WOOP_LIST_STATES = [
  '20-go-to-woop',
  '30-unoptimized-workloads',
  '40-go-to-autoscaler-settings',
];

type OpWorkloadListItem = {
  id?: string;
  workload?: { id?: string };
};

const findOpDemoWorkload = async (workloadId?: string) => {
  if (!workloadId) {
    return undefined;
  }

  const tourStepId = getTourStepIdFromUrl();
  const statesToTry = [
    ...(tourStepId ? [tourStepId] : []),
    ...OP_WOOP_LIST_STATES.filter((s) => s !== tourStepId),
  ];

  for (const state of statesToTry) {
    const list = await opDemoLoader.loadFixture({
      feature: 'workloadOptimization',
      endpoint: 'getWorkloads',
      state,
    });
    const workloads: OpWorkloadListItem[] = list?.workloads ?? [];
    const match = workloads.find(
      (item) => item.id === workloadId || item.workload?.id === workloadId
    );
    if (match) {
      return match;
    }
  }

  return undefined;
};

export const workloadOptimization: FeatureMiddleware<'workloadOptimization'> = {
  getAgentStatus: async ({ args, next, resolve }) => {
    if (isWoopDemoRequest(args[0])) {
      return resolve({
        clusterId: args[0]?.clusterId || DEMO_CLUSTER_ID,
        status: WorkloadOptimizationAgentStatus.RUNNING,
        currentVersion: '2',
        latestVersion: '2',
        inPlaceResizeEnabled: false,
        workloadAutoscalerReplicaCount: 1,
        updatedAt: new Date().toISOString(),
        psiMetricsSupported: false,
        hpaConvertersSupportedFromVersion: '1',
        scopedRecommendationsSupportedFromVersion: '1',
      });
    }
    return next();
  },

  getWorkloads: async (args) => {
    return handleOpDemoInterceptor({
      ...args,
      demoFixtureLoader: demoWoopWorkloadsList,
      demoClusterFallback: demoWoopWorkloadsList,
    });
  },

  getWorkload: async ({ args, next, resolve }) => {
    if (isWoopDemoRequest(args[0])) {
      const workloadId = args[0]?.workloadId;
      const opWorkload = await findOpDemoWorkload(workloadId);
      if (opWorkload) {
        return resolve(
          (opWorkload.workload
            ? opWorkload
            : { workload: opWorkload }) as OptimizationWorkloadDetailsResponse
        );
      }

      const fixture: OptimizationWorkloadDetailsResponse[] =
        await demoWoopWorkloadDetails();
      const workloadDetails = fixture.find(
        ({ workload }) => workload.id === workloadId
      );
      if (workloadDetails) {
        return resolve(workloadDetails);
      }

      // Keep demo traffic offline — missing detail fixtures must not hit the API.
      return rejectWithErrorMessage(
        `Demo workload details not found for ${workloadId ?? 'unknown'}`
      );
    }
    return next();
  },

  updateWorkload: async ({ args, next }) => {
    if (
      getIsOpDemoMode() ||
      getIsClusterDemoMode() ||
      isSimulatedDemoClusterRequest(args[0])
    ) {
      return rejectDemoClusterRequest();
    }
    return next();
  },

  getWorkloadSpec: async ({ args, next, resolve }) => {
    if (isWoopDemoRequest(args[0])) {
      const workloadId = args[0]?.workloadId;
      const fixture: WorkloadSpecResponse[] = await demoWoopWorkloadSpec();
      const workloadSpec = fixture.find(({ id }) => id === workloadId);
      if (workloadSpec) {
        return resolve(workloadSpec);
      }

      return rejectWithErrorMessage(
        `Demo workload spec not found for ${workloadId ?? 'unknown'}`
      );
    }
    return next();
  },

  getWorkloadOptimizationPolicies: async (args) => {
    return handleOpDemoInterceptor({
      ...args,
      demoFixtureLoader: demoWoopScalingPoliciesList,
      demoClusterFallback: demoWoopScalingPoliciesList,
    });
  },

  getWorkloadsOptimizationSummary: async (args) => {
    return handleOpDemoInterceptor({
      ...args,
      demoFixtureLoader: demoWoopWorkloadsSummary,
      demoClusterFallback: demoWoopWorkloadsSummary,
    });
  },

  getWorkloadsResourceQuotas: async ({ args, next, resolve }) => {
    if (isWoopDemoRequest(args[0])) {
      const data = await demoWoopResourceQuotasList();
      return resolve(data);
    }
    return next();
  },

  getWorkloadsLimitRanges: async ({ args, next, resolve }) => {
    if (isWoopDemoRequest(args[0])) {
      const data = await demoWoopLimitRangesList();
      return resolve(data);
    }
    return next();
  },

  getWorkloadsSummaryMetrics: async ({ args, next, resolve }) => {
    if (isWoopDemoRequest(args[0])) {
      const data = await demoWoopWorkloadsSummaryMetrics(
        args[0].fromTime,
        args[0].toTime
      );
      return resolve(data);
    }
    return next();
  },

  getEvents: async ({ args, next, orig, resolve }) => {
    if (isWoopDemoRequest(args[0])) {
      return resolve({ items: [] });
    }
    const [endpointArgs = {} as (typeof args)[0], ...rest] = args;

    if (endpointArgs?.pageLimit) {
      return next();
    }

    const aggregatedEvents: WorkloadOptimizationEvent[] = [];
    let lastResponse: Awaited<ReturnType<typeof orig>>;
    let nextCursor: string | undefined;

    while (true) {
      lastResponse = await orig(
        { ...endpointArgs, pageCursor: nextCursor, pageLimit: '500' },
        ...rest
      );
      if (lastResponse?.data?.items) {
        aggregatedEvents.push(...lastResponse.data.items);
      }
      if (!lastResponse.data.nextCursor) {
        break;
      }
      nextCursor = lastResponse.data.nextCursor;
    }

    lastResponse.data.items = aggregatedEvents;

    return lastResponse;
  },

  getWorkloadFilters: async (args) => {
    return handleOpDemoInterceptor({
      ...args,
      demoFixtureLoader: demoWoopWorkloadFilters,
      demoClusterFallback: demoWoopWorkloadFilters,
    });
  },
};
